import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import DOMPurify from 'dompurify';
import {
  ClipboardDocumentIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowsUpDownIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import Notification from "../components/Notification";
import { useStudioPlan } from "../hooks/useStudioPlan";
import { parsePlanLimitError } from "../plans/planLimitErrors";
// REMOVE: import { saveAs } from "file-saver";

interface WaiverField {
  id: string;
  type:
    | "name"
    | "email"
    | "phone"
    | "address"
    | "dateOfBirth"
    | "allergies"
    | "emergencyContactName"
    | "emergencyContactPhone"
    | "parentGuardianName"
    | "parentGuardianPhone"
    | "eSignature"
    | "customCheckbox"
    | "smsOptIn";
  label: string;
  required: boolean;
  included: boolean;
  order: number;
  studioName?: string; // For SMS Opt-In field
}

interface Waiver {
  id: string;
  title: string;
  content: string;
  fields: WaiverField[];
  is_active: boolean;
  send_confirmation_email: boolean;
  created_at: string;
  updated_at: string;
  signed_count: number;
}

const AVAILABLE_FIELDS: Omit<WaiverField, "id" | "order">[] = [
  { type: "name", label: "Full Name", required: true, included: true },
  { type: "email", label: "Email Address", required: true, included: true },
  { type: "phone", label: "Phone Number", required: false, included: false },
  { type: "address", label: "Address", required: false, included: false },
  {
    type: "dateOfBirth",
    label: "Date of Birth",
    required: false,
    included: false,
  },
  {
    type: "allergies",
    label: "Allergies/Medical Information",
    required: false,
    included: false,
  },
  {
    type: "emergencyContactName",
    label: "Emergency Contact Name",
    required: false,
    included: false,
  },
  {
    type: "emergencyContactPhone",
    label: "Emergency Contact Phone",
    required: false,
    included: false,
  },
  {
    type: "parentGuardianName",
    label: "Parent/Guardian Name",
    required: false,
    included: false,
  },
  {
    type: "parentGuardianPhone",
    label: "Parent/Guardian Phone",
    required: false,
    included: false,
  },
  {
    type: "eSignature",
    label: "Electronic Signature",
    required: false,
    included: false,
  },
];

const FIELD_ICONS = {
  name: UserIcon,
  email: EnvelopeIcon,
  phone: PhoneIcon,
  address: MapPinIcon,
  dateOfBirth: CalendarIcon,
  allergies: ExclamationTriangleIcon,
  emergencyContactName: ExclamationTriangleIcon,
  emergencyContactPhone: PhoneIcon,
  parentGuardianName: UserIcon,
  parentGuardianPhone: PhoneIcon,
  eSignature: HandRaisedIcon,
  customCheckbox: CheckCircleIcon,
  smsOptIn: PhoneIcon,
};

const CSV_HEADERS = [
  "Full Name",
  "Email Address",
  "Phone Number",
  "Address",
  "Date of Birth",
  "Allergies/Medical Information",
  "Emergency Contact Name",
  "Emergency Contact Phone",
  "Parent/Guardian Name",
  "Parent/Guardian Phone",
  "Electronic Signature",
];

function toCsvRow(sub: any) {
  // Try both student_info and form_data for each field
  const info = sub.student_info || {};
  const form = sub.form_data || {};
  return [
    info.name ||
      info.fullName ||
      info.full_name ||
      info.firstName ||
      info.first_name ||
      form.name ||
      form.fullName ||
      form.full_name ||
      form.firstName ||
      form.first_name ||
      "",
    info.email ||
      info.emailAddress ||
      info.email_address ||
      form.email ||
      form.emailAddress ||
      form.email_address ||
      "",
    info.phone || form.phone || "",
    info.address || form.address || "",
    info.dateOfBirth || info.dob || form.dateOfBirth || form.dob || "",
    info.allergies || form.allergies || "",
    info.emergencyContactName || form.emergencyContactName || "",
    info.emergencyContactPhone || form.emergencyContactPhone || "",
    info.parentGuardianName || form.parentGuardianName || "",
    info.parentGuardianPhone || form.parentGuardianPhone || "",
    info.eSignature ||
      info.signature ||
      form.eSignature ||
      form.signature ||
      "",
  ];
}

function escapeCsvValue(value: string) {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function arrayToCsv(rows: string[][]) {
  return rows.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
}

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`;
};

// Phone number formatting function
function formatPhoneNumber(phone: string | null): string {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
}

// Date input formatter function
const formatDateInput = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Convert waiver date (MM/DD/YYYY or YYYY-MM-DD) to YYYY-MM-DD for students table
const waiverDateToStorageFormat = (dateString: string | null | undefined): string | null => {
  if (!dateString || !String(dateString).trim()) return null;
  const s = String(dateString).trim();
  if (s.match(/^\d{4}-\d{2}-\d{2}$/)) return s;
  try {
    const parts = s.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      const monthNum = Number(month);
      const dayNum = Number(day);
      const yearNum = Number(year);
      if (isNaN(monthNum) || isNaN(dayNum) || isNaN(yearNum)) return null;
      if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || yearNum < 1900 || yearNum > 2100) return null;
      return `${year}-${month}-${day}`;
    }
  } catch {
    // try parsing as ISO date
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  return null;
};

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  title: 200,
  content: 10000, // Allow longer content for waiver text
  fieldLabel: 100,
  searchTerm: 100,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For waiver content, allow more formatting but still remove dangerous content
  if (fieldType === 'content') {
    // Remove dangerous patterns but allow basic formatting
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    // Don't trim content to preserve formatting
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.trim();
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'title':
      // Title should not be empty and should be reasonable length
      return sanitized.length >= 1 && sanitized.length <= MAX_LENGTHS.title;
    case 'content':
      // Content should not be empty
      return sanitized.length >= 1 && sanitized.length <= MAX_LENGTHS.content;
    case 'searchTerm':
      // Search term validation
      const searchDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !searchDangerousPatterns.test(sanitized) && sanitized.length <= MAX_LENGTHS.searchTerm;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Waivers Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

export default function Waivers() {
  const { getAuthToken } = useAuth();
  const { plan } = useStudioPlan();
  const waiverLimit = plan?.capabilities.maxWaivers ?? 5;
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedWaiver, setSelectedWaiver] = useState<Waiver | null>(null);
  const [signatureData, setSignatureData] = useState<string>("");
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    fields: [] as WaiverField[],
    send_confirmation_email: true,
  });

  // Add state for submissions modal
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState<any | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [submissionsWaiver, setSubmissionsWaiver] = useState<Waiver | null>(
    null
  );

  // Add state for delete confirmation modal
  const [waiverToDelete, setWaiverToDelete] = useState<Waiver | null>(null);
  const [submissionToDelete, setSubmissionToDelete] = useState<any | null>(null);

  // Add state to track submission counts for each waiver
  const [submissionCounts, setSubmissionCounts] = useState<
    Record<string, number>
  >({});

  // Add state for expanding E Signature in the submission details modal
  const [showFullESignature, setShowFullESignature] = useState(false);

  // State for editing custom checkbox labels
  const [editingCheckboxLabels, setEditingCheckboxLabels] = useState<Record<string, { editing: boolean; tempLabel: string }>>({});

  // State for custom checkbox creation modal
  const [showCustomCheckboxModal, setShowCustomCheckboxModal] = useState(false);
  const [newCheckboxLabel, setNewCheckboxLabel] = useState("");

  // State for SMS Opt-In creation modal
  const [showSmsOptInModal, setShowSmsOptInModal] = useState(false);
  const [smsOptInStudioName, setSmsOptInStudioName] = useState("");

  // State for editing SMS Opt-In studio names
  const [editingSmsStudioNames, setEditingSmsStudioNames] = useState<Record<string, { editing: boolean; tempStudioName: string }>>({});

  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: string;
    title?: string;
  }>({
    show: false,
    message: "",
    type: "general",
  });
  const closeNotification = () =>
    setNotification({ ...notification, show: false });

  const [exporting, setExporting] = useState(false);

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  useEffect(() => {
    fetchCsrfToken();
    fetchWaivers();
  }, []);



  useEffect(() => {
    if (showCreateModal) {
      setFormData({
        title: "",
        content: "",
        fields: [
          {
            id: `name-${Date.now()}`,
            type: "name",
            label: "Full Name",
            required: true,
            included: true,
            order: 0,
          },
          {
            id: `email-${Date.now() + 1}`,
            type: "email",
            label: "Email Address",
            required: true,
            included: true,
            order: 1,
          },
        ],
        send_confirmation_email: true,
      });
    }
  }, [showCreateModal]);

  const fetchWaivers = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWaivers(data);
      } else {
        setNotification({
          show: true,
          message: "Failed to fetch waivers",
          type: "general",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error loading waivers",
        type: "general",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWaiver = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        type: "general",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'createWaiver' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          type: "general",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(formData.title, 'title')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'title', value: formData.title });
      setNotification({
        show: true,
        message: "Invalid waiver title",
        type: "general",
      });
      return;
    }

    if (!validateInput(formData.content, 'content')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'content', value: formData.content.substring(0, 100) + '...' });
      setNotification({
        show: true,
        message: "Invalid waiver content",
        type: "general",
      });
      return;
    }

    // Check waiver limit
    if (waivers.length >= waiverLimit) {
      setNotification({
        show: true,
        message: `Maximum number of waivers (${waiverLimit}) reached`,
        type: "general",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) return;

    setLastSubmissionTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            title: sanitizeInput(formData.title, 'title'),
            content: sanitizeInput(formData.content, 'content'),
            fields: formData.fields,
            send_confirmation_email: formData.send_confirmation_email,
          }),
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Waiver created successfully",
          type: "success",
        });
        setShowCreateModal(false);
        setFormData({ title: "", content: "", fields: [], send_confirmation_email: true });
        fetchWaivers();
      } else {
        setNotification({
          show: true,
          message: "Failed to create waiver",
          type: "general",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error creating waiver",
        type: "general",
      });
    }
  };

  const handleUpdateWaiver = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWaiver) return;

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        type: "general",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'updateWaiver', waiverId: selectedWaiver.id });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          type: "general",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(formData.title, 'title')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'title', value: formData.title, waiverId: selectedWaiver.id });
      setNotification({
        show: true,
        message: "Invalid waiver title",
        type: "general",
      });
      return;
    }

    if (!validateInput(formData.content, 'content')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'content', value: formData.content.substring(0, 100) + '...', waiverId: selectedWaiver.id });
      setNotification({
        show: true,
        message: "Invalid waiver content",
        type: "general",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!authToken) return;

    setLastSubmissionTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${selectedWaiver.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            title: sanitizeInput(formData.title, 'title'),
            content: sanitizeInput(formData.content, 'content'),
            fields: formData.fields,
            send_confirmation_email: formData.send_confirmation_email,
          }),
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Waiver updated successfully",
          type: "success",
        });
        setShowEditModal(false);
        setSelectedWaiver(null);
        setFormData({ title: "", content: "", fields: [], send_confirmation_email: true });
        fetchWaivers();
      } else {
        setNotification({
          show: true,
          message: "Failed to update waiver",
          type: "general",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error updating waiver",
        type: "general",
      });
    }
  };

  const handleDeleteWaiver = (waiver: Waiver) => {
    setWaiverToDelete(waiver);
  };

  const confirmDelete = async () => {
    if (!waiverToDelete) return;

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        type: "general",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteWaiver', waiverId: waiverToDelete.id });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          type: "general",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    const authToken = getAuthToken();
    if (!authToken) return;

    setLastSubmissionTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${waiverToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Waiver deleted successfully",
          type: "success",
        });
        setWaiverToDelete(null);
        fetchWaivers();
      } else {
        setNotification({
          show: true,
          message: "Failed to delete waiver",
          type: "general",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error deleting waiver",
        type: "general",
      });
    }
  };

  const cancelDelete = () => {
    setWaiverToDelete(null);
  };

  const confirmDeleteSubmission = async () => {
    if (!submissionToDelete) return;

    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waiver-submissions/${submissionToDelete.id}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      if (response.ok) {
        setSubmissions((subs) =>
          subs.filter((s) => s.id !== submissionToDelete.id)
        );
        setNotification({
          show: true,
          message: "Submission deleted successfully",
          type: "success",
        });
      } else {
        setNotification({
          show: true,
          message: "Failed to delete submission",
          type: "general",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error deleting submission",
        type: "general",
      });
    } finally {
      setSubmissionToDelete(null);
    }
  };

  const cancelDeleteSubmission = () => {
    setSubmissionToDelete(null);
  };

  const handleToggleActive = async (
    waiverId: string,
    currentStatus: boolean
  ) => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${waiverId}/toggle`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ is_active: !currentStatus }),
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: `Waiver ${!currentStatus ? "activated" : "deactivated"}`,
          type: "success",
        });
        fetchWaivers();
      } else {
        setNotification({
          show: true,
          message: "Failed to update waiver status",
          type: "general",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error updating waiver status",
        type: "general",
      });
    }
  };

  const openEditModal = (waiver: Waiver) => {
    setSelectedWaiver(waiver);
    setFormData({
      title: waiver.title,
      content: waiver.content,
      fields: waiver.fields || [],
      send_confirmation_email: waiver.send_confirmation_email ?? true,
    });
    setShowEditModal(true);
  };

  const openViewModal = (waiver: Waiver) => {
    setSelectedWaiver(waiver);
    setShowViewModal(true);
  };

  const copyToClipboard = (text: string) => {
    // Sanitize content before copying to clipboard
    const sanitizedText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    navigator.clipboard.writeText(sanitizedText);
    setNotification({
      show: true,
      message: "Waiver content copied to clipboard",
      type: "success",
    });
  };

  const openSignatureModal = () => {
    setShowSignatureModal(true);
    setSignatureData("");
  };

  const closeSignatureModal = () => {
    setShowSignatureModal(false);
    setSignatureData("");
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignatureData("");
      }
    }
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dataURL = canvas.toDataURL("image/png");
      setSignatureData(dataURL);
      setShowSignatureModal(false);
      setNotification({
        show: true,
        message: "Signature saved successfully",
        type: "success",
      });
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#000";
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        ctx.lineTo(x, y);
        ctx.stroke();
      }
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const toggleField = (fieldType: WaiverField["type"]) => {
    if (fieldType === "name" || fieldType === "email") return; // Do not allow removing
    const existingField = formData.fields.find((f) => f.type === fieldType);

    if (existingField) {
      // Remove field
      setFormData({
        ...formData,
        fields: formData.fields.filter((f) => f.type !== fieldType),
      });
    } else {
      // Add field
      const newField: WaiverField = {
        id: `${fieldType}-${Date.now()}`,
        type: fieldType,
        label: AVAILABLE_FIELDS.find((f) => f.type === fieldType)?.label || "",
        required: false,
        included: true,
        order: formData.fields.length,
      };
      setFormData({
        ...formData,
        fields: [...formData.fields, newField],
      });
    }
  };

  const openCustomCheckboxModal = () => {
    setNewCheckboxLabel("");
    setShowCustomCheckboxModal(true);
  };

  const closeCustomCheckboxModal = () => {
    setShowCustomCheckboxModal(false);
    setNewCheckboxLabel("");
  };

  const addCustomCheckbox = () => {
    if (newCheckboxLabel && newCheckboxLabel.trim()) {
      const sanitizedLabel = sanitizeInput(newCheckboxLabel.trim(), 'fieldLabel');
      const newField: WaiverField = {
        id: `customCheckbox-${Date.now()}`,
        type: "customCheckbox",
        label: sanitizedLabel,
        required: false,
        included: true,
        order: formData.fields.length,
      };
      setFormData({
        ...formData,
        fields: [...formData.fields, newField],
      });
      closeCustomCheckboxModal();
    }
  };

  const updateCustomCheckboxLabel = (fieldId: string, newLabel: string) => {
    const sanitizedLabel = sanitizeInput(newLabel.trim(), 'fieldLabel');
    if (sanitizedLabel) {
      setFormData({
        ...formData,
        fields: formData.fields.map((f) =>
          f.id === fieldId ? { ...f, label: sanitizedLabel } : f
        ),
      });
    }
  };

  const openSmsOptInModal = () => {
    setSmsOptInStudioName("");
    setShowSmsOptInModal(true);
  };

  const closeSmsOptInModal = () => {
    setShowSmsOptInModal(false);
    setSmsOptInStudioName("");
  };

  const addSmsOptIn = () => {
    const sanitizedStudioName = sanitizeInput(smsOptInStudioName.trim(), 'fieldLabel');
    const defaultText = "I agree to receive SMS messages from ______ regarding class reminders, student alerts, and schedule updates. Message frequency varies. Standard message and data rates may apply. I understand I can reply STOP at any time to unsubscribe.";
    const labelText = defaultText.replace("______", sanitizedStudioName || "[Studio Name]");
    
    const newField: WaiverField = {
      id: `smsOptIn-${Date.now()}`,
      type: "smsOptIn",
      label: labelText,
      required: true,
      included: true,
      order: formData.fields.length,
      studioName: sanitizedStudioName || "",
    };
    setFormData({
      ...formData,
      fields: [...formData.fields, newField],
    });
    closeSmsOptInModal();
  };

  const updateSmsOptInStudioName = (fieldId: string, newStudioName: string) => {
    const sanitizedStudioName = sanitizeInput(newStudioName.trim(), 'fieldLabel');
    const defaultText = "I agree to receive SMS messages from ______ regarding class reminders, student alerts, and schedule updates. Message frequency varies. Standard message and data rates may apply. I understand I can reply STOP at any time to unsubscribe.";
    const labelText = defaultText.replace("______", sanitizedStudioName || "[Studio Name]");
    
    setFormData({
      ...formData,
      fields: formData.fields.map((f) =>
        f.id === fieldId ? { ...f, studioName: sanitizedStudioName, label: labelText } : f
      ),
    });
  };

  const toggleFieldRequired = (fieldType: WaiverField["type"], fieldId?: string) => {
    setFormData({
      ...formData,
      fields: formData.fields.map((f) =>
        (fieldId ? f.id === fieldId : f.type === fieldType) ? { ...f, required: !f.required } : f
      ),
    });
  };

  const moveField = (
    fieldType: WaiverField["type"],
    direction: "up" | "down",
    fieldId?: string
  ) => {
    const currentIndex = formData.fields.findIndex((f) => 
      fieldId ? f.id === fieldId : f.type === fieldType
    );
    if (currentIndex === -1) return;

    const newFields = [...formData.fields];
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < newFields.length) {
      [newFields[currentIndex], newFields[newIndex]] = [
        newFields[newIndex],
        newFields[currentIndex],
      ];
      setFormData({
        ...formData,
        fields: newFields.map((f, index) => ({ ...f, order: index })),
      });
    }
  };

  const renderFieldPreview = (field: WaiverField) => {
    const IconComponent = FIELD_ICONS[field.type];

    switch (field.type) {
      case "name":
      case "email":
      case "phone":
      case "emergencyContactName":
      case "emergencyContactPhone":
      case "parentGuardianName":
      case "parentGuardianPhone":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type={field.type === "email" ? "email" : "text"}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder={`Enter ${field.label.toLowerCase()}`}
              disabled
            />
          </div>
        );

      case "address":
      case "allergies":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder={field.type === "address" ? "Enter your address" : "List any allergies (food, medication, environmental, etc.)"}
              disabled
            />
          </div>
        );

      case "dateOfBirth":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="MM/DD/YYYY"
              disabled
            />
          </div>
        );

      case "eSignature":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {field.label}{" "}
              {field.required && <span className="text-red-500">*</span>}
            </label>
            <div
              className="w-full h-20 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex items-center justify-center bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              onClick={openSignatureModal}
            >
              {signatureData ? (
                <img
                  src={signatureData}
                  alt="Signature"
                  className="max-h-16 max-w-full object-contain"
                />
              ) : (
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  Click to sign
                </span>
              )}
            </div>
          </div>
        );

      case "customCheckbox":
        return (
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                disabled
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-100">
                {field.label}{" "}
                {field.required && <span className="text-red-500">*</span>}
              </span>
            </label>
          </div>
        );

      case "smsOptIn":
        return (
          <div className="mb-4">
            <label className="flex items-start">
              <input
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 mt-0.5 flex-shrink-0"
                disabled
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-100">
                {field.label}{" "}
                {field.required && <span className="text-red-500">*</span>}
              </span>
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  const fetchSubmissions = async (waiver: Waiver, search: string = "") => {
    setSubmissionsLoading(true);
    setSubmissionsError("");
    setSubmissionsWaiver(waiver);
    try {
      const authToken = getAuthToken();
      if (!authToken) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${
        waiver.id
      }/submissions${search ? `?search=${encodeURIComponent(search)}` : ""}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      } else {
        setSubmissionsError("Failed to fetch submissions");
      }
    } catch (error) {
      setSubmissionsError("Error loading submissions");
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const openSubmissionsModal = (waiver: Waiver) => {
    setShowSubmissionsModal(true);
    setSearchTerm("");
    setSelectedSubmission(null);
    fetchSubmissions(waiver);
  };

  const closeSubmissionsModal = () => {
    setShowSubmissionsModal(false);
    setSubmissions([]);
    setSelectedSubmission(null);
    setSubmissionsWaiver(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    
    // Validate search input
    if (!validateInput(searchValue, 'searchTerm')) {
      logSecurityEvent('INVALID_SEARCH_ATTEMPT', { searchValue: searchValue.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid search term",
        type: "general",
      });
      return;
    }
    
    const sanitizedSearch = sanitizeInput(searchValue, 'searchTerm');
    setSearchTerm(sanitizedSearch);
    
    if (submissionsWaiver) {
      fetchSubmissions(submissionsWaiver, sanitizedSearch);
    }
  };

  const openSubmissionDetail = async (submissionId: string) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waiver-submissions/${submissionId}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedSubmission(data);
      } else {
        setSelectedSubmission(null);
      }
    } catch {
      setSelectedSubmission(null);
    }
  };

  const convertToLead = async (submission: any) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Get studio ID from the user context or fetch it
      let studioId: string;

      // First try to get studio ID from the user context
      const userResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (userResponse.ok) {
        const userData = await userResponse.json();
        studioId = userData.id;
      } else {
        setNotification({
          show: true,
          message: "Failed to get studio information",
          type: "general",
        });
        return;
      }

      const leadData = {
        name:
          submission.student_info?.name ||
          submission.student_info?.fullName ||
          submission.student_info?.full_name ||
          submission.student_info?.firstName ||
          submission.student_info?.first_name ||
          "Unknown",
        email:
          submission.student_info?.email ||
          submission.student_info?.emailAddress ||
          submission.student_info?.email_address ||
          "",
        phone: submission.student_info?.phone || "",
        birthdate: waiverDateToStorageFormat(
          submission.student_info?.dateOfBirth ||
            submission.student_info?.dob ||
            submission.form_data?.dateOfBirth ||
            submission.form_data?.dob ||
            null
        ),
        source: "Waiver Submission",
        studio_id: studioId,
        waiver_signed: true, // Mark as having signed a waiver
        notes: `Converted from waiver submission (${
          submissionsWaiver?.title || "Unknown Waiver"
        }) on ${new Date(submission.submitted_at).toLocaleDateString()}`,
        created_at: new Date().toISOString(),
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads?checkDuplicates=true`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(leadData),
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Successfully converted to lead",
          type: "success",
        });

        // Update the submission in the database to mark it as converted
        const updateResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waiver-submissions/${submission.id}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              converted_to_lead: true,
            }),
          }
        );

        if (updateResponse.ok) {
          // Refresh the submissions to show updated status
          if (submissionsWaiver) {
            fetchSubmissions(submissionsWaiver, searchTerm);
          }
        }
      } else {
        const limitError = await parsePlanLimitError(response, 1);
        setNotification({
          show: true,
          message: limitError?.message ?? "Failed to convert to lead. Please try again.",
          type: "general",
          title: limitError?.title ?? "Error",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error converting to lead",
        type: "general",
      });
    }
  };

  const convertToStudent = async (submission: any) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Get studio ID from the user context or fetch it
      let studioId: string;

      // First try to get studio ID from the user context
      const userResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (userResponse.ok) {
        const userData = await userResponse.json();
        studioId = userData.id;
      } else {
        setNotification({
          show: true,
          message: "Failed to get studio information",
          type: "general",
        });
        return;
      }

      const studentData = {
        name:
          submission.student_info?.name ||
          submission.student_info?.fullName ||
          submission.student_info?.full_name ||
          submission.student_info?.firstName ||
          submission.student_info?.first_name ||
          "Unknown",
        email:
          submission.student_info?.email ||
          submission.student_info?.emailAddress ||
          submission.student_info?.email_address ||
          "",
        phone: submission.student_info?.phone || submission.form_data?.phone || "",
        birthdate: waiverDateToStorageFormat(
          submission.student_info?.dateOfBirth ||
            submission.student_info?.dob ||
            submission.form_data?.dateOfBirth ||
            submission.form_data?.dob ||
            null
        ),
        age: (() => {
          const birthdate =
            submission.student_info?.dateOfBirth ||
            submission.student_info?.dob ||
            submission.form_data?.dateOfBirth ||
            submission.form_data?.dob;
          if (!birthdate) return null;
          const today = new Date();
          const birthDate = new Date(birthdate);
          if (isNaN(birthDate.getTime())) return null;
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          return age;
        })(),
        emergency_contact_name:
          submission.student_info?.emergencyContactName || "",
        emergency_contact_phone:
          submission.student_info?.emergencyContactPhone || "",
        studio_id: studioId,
        status: "active",
        join_date: new Date().toISOString(),
        waiver_signed: true,
        notes: `Converted from waiver submission (${
          submissionsWaiver?.title || "Unknown Waiver"
        }) on ${new Date(
          submission.submitted_at
        ).toLocaleDateString()}. Parent/Guardian: ${
          submission.student_info?.parentGuardianName || "Not provided"
        } (${submission.student_info?.parentGuardianPhone || "No phone"})`,
        created_at: new Date().toISOString(),
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(studentData),
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: "Successfully converted to student",
          type: "success",
        });

        // Update the submission in the database to mark it as converted
        const updateResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waiver-submissions/${submission.id}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              converted_to_student: true,
            }),
          }
        );

        if (updateResponse.ok) {
          // Refresh the submissions to show updated status
          if (submissionsWaiver) {
            fetchSubmissions(submissionsWaiver, searchTerm);
          }
        }
      } else {
        const limitError = await parsePlanLimitError(response, 1);
        setNotification({
          show: true,
          message: limitError?.message ?? "Failed to convert to student. Please try again.",
          type: "general",
          title: limitError?.title ?? "Error",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error converting to student",
        type: "general",
      });
    }
  };

  // Fetch submission counts for all waivers after waivers are loaded
  useEffect(() => {
    const fetchCounts = async () => {
      const authToken = getAuthToken();
      if (!authToken || waivers.length === 0) return;
      const counts: Record<string, number> = {};
      for (const waiver of waivers) {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${waiver.id}/submissions`;
        try {
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (response.ok) {
            const data = await response.json();
            counts[waiver.id] = data.length;
          } else {
            counts[waiver.id] = 0;
          }
        } catch {
          counts[waiver.id] = 0;
        }
      }
      setSubmissionCounts(counts);
    };
    if (waivers.length > 0) fetchCounts();
  }, [waivers]);

  // Export individual waiver data as JSON
  const exportWaiverData = async (waiver: Waiver) => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Fetch submissions for this waiver
      let submissions: any[] = [];
      const submissionsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${waiver.id}/submissions`;
      const response = await fetch(submissionsUrl, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      
      if (response.ok) {
        submissions = await response.json();
      }

      // Create a field mapping for custom fields
      const fieldMapping: Record<string, string> = {};
      if (waiver.fields) {
        waiver.fields.forEach((field: WaiverField) => {
          if (field.type === 'customCheckbox' || field.type === 'smsOptIn') {
            fieldMapping[field.id] = field.label;
          }
        });
      }

      // Transform submissions to include readable field names
      const transformedSubmissions = submissions.map(sub => {
        const transformed = { ...sub };
        
        // Transform form_data
        if (transformed.form_data) {
          const newFormData: Record<string, any> = {};
          
          // First, add all existing fields with readable names
          Object.entries(transformed.form_data).forEach(([key, value]) => {
            const readableKey = fieldMapping[key] || key;
            newFormData[readableKey] = value;
          });
          
          // Then, add any missing custom checkbox/SMS fields as "not agreed"
          if (waiver.fields) {
            waiver.fields.forEach((field: WaiverField) => {
              if (field.type === 'customCheckbox' || field.type === 'smsOptIn') {
                const readableKey = field.label;
                if (!(field.id in transformed.form_data) && !(readableKey in newFormData)) {
                  newFormData[readableKey] = false;
                }
              }
            });
          }
          
          transformed.form_data = newFormData;
          transformed.form_data_with_readable_names = newFormData;
        }

        // Transform student_info
        if (transformed.student_info) {
          const newStudentInfo: Record<string, any> = {};
          
          // Add all existing fields with readable names
          Object.entries(transformed.student_info).forEach(([key, value]) => {
            const readableKey = fieldMapping[key] || key;
            newStudentInfo[readableKey] = value;
          });
          
          // Add any missing custom checkbox/SMS fields as "not agreed"
          if (waiver.fields) {
            waiver.fields.forEach((field: WaiverField) => {
              if (field.type === 'customCheckbox' || field.type === 'smsOptIn') {
                const readableKey = field.label;
                if (!(field.id in transformed.student_info) && !(readableKey in newStudentInfo)) {
                  newStudentInfo[readableKey] = false;
                }
              }
            });
          }
          
          transformed.student_info = newStudentInfo;
        }

        return transformed;
      });

      const exportData = {
        waiver: {
          id: waiver.id,
          title: waiver.title,
          content: waiver.content,
          fields: waiver.fields,
          is_active: waiver.is_active,
          created_at: waiver.created_at,
          updated_at: waiver.updated_at,
          public_link: `${window.location.origin}/waiver/${waiver.id}`,
        },
        field_mapping: fieldMapping,
        submissions: transformedSubmissions,
        exported_at: new Date().toISOString(),
        export_metadata: {
          total_submissions: submissions.length,
          waiver_fields_count: waiver.fields?.length || 0,
          converted_to_leads: submissions.filter(s => s.converted_to_lead).length,
          converted_to_students: submissions.filter(s => s.converted_to_student).length,
        }
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${waiver.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setNotification({
        show: true,
        message: "Waiver data exported successfully",
        type: "success",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to export waiver data",
        type: "general",
      });
    }
  };

  // Export all waiver data as JSON
  const handleExportAllWaiversJSON = async () => {
    setExporting(true);
    try {
      const authToken = getAuthToken();
      if (!authToken) return;
      
      const allWaiverData = [];
      for (const waiver of waivers) {
        const submissionsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${waiver.id}/submissions`;
        const response = await fetch(submissionsUrl, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        
        let submissions: any[] = [];
        if (response.ok) {
          submissions = await response.json();
        }

        // Create a field mapping for custom fields
        const fieldMapping: Record<string, string> = {};
        if (waiver.fields) {
          waiver.fields.forEach((field: WaiverField) => {
            if (field.type === 'customCheckbox' || field.type === 'smsOptIn') {
              fieldMapping[field.id] = field.label;
            }
          });
        }

        // Transform submissions to include readable field names
        const transformedSubmissions = submissions.map(sub => {
          const transformed = { ...sub };
          
          // Transform form_data
          if (transformed.form_data) {
            const newFormData: Record<string, any> = {};
            
            // First, add all existing fields with readable names
            Object.entries(transformed.form_data).forEach(([key, value]) => {
              const readableKey = fieldMapping[key] || key;
              newFormData[readableKey] = value;
            });
            
            // Then, add any missing custom checkbox/SMS fields as "not agreed"
            if (waiver.fields) {
              waiver.fields.forEach((field: WaiverField) => {
                if (field.type === 'customCheckbox' || field.type === 'smsOptIn') {
                  const readableKey = field.label;
                  if (!(field.id in transformed.form_data) && !(readableKey in newFormData)) {
                    newFormData[readableKey] = false;
                  }
                }
              });
            }
            
            transformed.form_data = newFormData;
          }

          // Transform student_info
          if (transformed.student_info) {
            const newStudentInfo: Record<string, any> = {};
            
            // Add all existing fields with readable names
            Object.entries(transformed.student_info).forEach(([key, value]) => {
              const readableKey = fieldMapping[key] || key;
              newStudentInfo[readableKey] = value;
            });
            
            // Add any missing custom checkbox/SMS fields as "not agreed"
            if (waiver.fields) {
              waiver.fields.forEach((field: WaiverField) => {
                if (field.type === 'customCheckbox' || field.type === 'smsOptIn') {
                  const readableKey = field.label;
                  if (!(field.id in transformed.student_info) && !(readableKey in newStudentInfo)) {
                    newStudentInfo[readableKey] = false;
                  }
                }
              });
            }
            
            transformed.student_info = newStudentInfo;
          }

          return transformed;
        });

        allWaiverData.push({
          waiver: {
            id: waiver.id,
            title: waiver.title,
            content: waiver.content,
            fields: waiver.fields,
            is_active: waiver.is_active,
            created_at: waiver.created_at,
            updated_at: waiver.updated_at,
            public_link: `${window.location.origin}/waiver/${waiver.id}`,
          },
          field_mapping: fieldMapping,
          submissions: transformedSubmissions,
          submission_metadata: {
            total_submissions: submissions.length,
            converted_to_leads: submissions.filter(s => s.converted_to_lead).length,
            converted_to_students: submissions.filter(s => s.converted_to_student).length,
          }
        });
      }

      const exportData = {
        waivers: allWaiverData,
        exported_at: new Date().toISOString(),
        export_metadata: {
          total_waivers: waivers.length,
          total_submissions: allWaiverData.reduce((sum, w) => sum + w.submissions.length, 0),
          active_waivers: waivers.filter(w => w.is_active).length,
          inactive_waivers: waivers.filter(w => !w.is_active).length,
        }
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const date = new Date().toISOString().split("T")[0];
      const filename = `all_waivers_data_${date}.json`;
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setNotification({
        show: true,
        message: "All waiver data exported successfully",
        type: "success",
      });
    } catch (e) {
      setNotification({
        show: true,
        message: "Failed to export waiver data",
        type: "general",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportWaiverData = async () => {
    setExporting(true);
    try {
      const authToken = getAuthToken();
      if (!authToken) return;
      
      // Collect all unique fields across all waivers
      const allFieldsMap = new Map();
      
      // Add standard fields first
      CSV_HEADERS.forEach((header, index) => {
        allFieldsMap.set(header, { header, index, type: 'standard' });
      });
      
      // Add custom fields from all waivers
      let customFieldIndex = CSV_HEADERS.length;
      for (const waiver of waivers) {
        if (waiver.fields) {
          waiver.fields.forEach((field: WaiverField) => {
            if (field.type === 'customCheckbox' || field.type === 'smsOptIn') {
              const fieldKey = `${field.id}`;
              if (!allFieldsMap.has(fieldKey)) {
                allFieldsMap.set(fieldKey, { 
                  header: field.label, 
                  index: customFieldIndex++, 
                  type: field.type,
                  fieldId: field.id 
                });
              }
            }
          });
        }
      }
      
      // Build dynamic headers
      const dynamicHeaders = Array.from(allFieldsMap.values())
        .sort((a, b) => a.index - b.index)
        .map(f => f.header);
      
      // Fetch all submissions
      let allSubmissions: any[] = [];
      for (const waiver of waivers) {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/waivers/${waiver.id}/submissions`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          // Attach waiver info to each submission for field mapping
          const dataWithWaiver = data.map((sub: any) => ({ ...sub, _waiver: waiver }));
          allSubmissions = allSubmissions.concat(dataWithWaiver);
        }
      }
      
      // Convert submissions to rows with dynamic fields
      const dataRows = allSubmissions.map(sub => {
        const info = sub.student_info || {};
        const form = sub.form_data || {};
        const row: string[] = [];
        
        allFieldsMap.forEach((fieldInfo) => {
          if (fieldInfo.type === 'standard') {
            // Use existing toCsvRow logic for standard fields
            const standardRow = toCsvRow(sub);
            row.push(standardRow[fieldInfo.index] || '');
          } else if (fieldInfo.type === 'customCheckbox') {
            // Get custom checkbox value
            const value = info[fieldInfo.fieldId] || form[fieldInfo.fieldId];
            row.push(value ? 'Agreed' : 'Not agreed');
          } else if (fieldInfo.type === 'smsOptIn') {
            // Get SMS opt-in value
            const value = info[fieldInfo.fieldId] || form[fieldInfo.fieldId];
            row.push(value ? 'Opted in' : 'Not opted in');
          }
        });
        
        return row;
      });
      
      const rows = [dynamicHeaders, ...dataRows];
      const csv = arrayToCsv(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const date = new Date().toISOString().split("T")[0];
      const filename = `waiver_submissions_${date}.csv`;
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setNotification({
        show: true,
        message: "Waiver submissions exported to CSV successfully",
        type: "success",
      });
    } catch (e) {
      setNotification({
        show: true,
        message: "Failed to export waiver data",
        type: "general",
      });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="relative bg-gray-50 dark:bg-gray-900">
        <div className="space-y-4 p-6">
          {/* Header - Always visible */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white pt-8 2xl:pt-6">
                Waivers
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage legal documents and waivers for your students
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <button
                disabled
                className="inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 opacity-50 cursor-not-allowed"
                title="Export all waiver submissions to CSV"
              >
                <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                Export Waiver Data
              </button>
              <button
                disabled
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 opacity-50 cursor-not-allowed"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Waiver
              </button>
            </div>
          </div>

          {/* Loading State */}
          <div className="bg-white dark:bg-gray-800 overflow-hidden rounded-lg shadow">
            <div className="flex justify-center items-center py-8">
              <div className="animate-pulse flex space-x-4 items-center">
                <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Loading waivers...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={closeNotification}
          type={notification.type as any}
        />
        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Waivers
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage legal documents and waivers for your students
            </p>
          </div>
          <div className="flex flex-wrap gap-2 xl:items-center">
            <button
              onClick={handleExportAllWaiversJSON}
              className="inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={exporting}
              title="Export all waiver data as JSON"
            >
              <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
              <span className="pointer-events-none">{exporting ? "Exporting..." : "Export Data"}</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                waivers.length >= waiverLimit
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              disabled={waivers.length >= waiverLimit}
            >
              <PlusIcon className="h-4 w-4 mr-2 pointer-events-none" />
              <span className="pointer-events-none">{waivers.length >= waiverLimit
                ? `Limit Reached (${waiverLimit}/${waiverLimit})`
                : `Create Waiver ${waivers.length}/${waiverLimit}`}</span>
            </button>
          </div>
        </div>

        {/* Waivers List */}
        <div className="bg-white dark:bg-gray-800 overflow-hidden rounded-lg shadow">
          {waivers.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardDocumentIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No waivers
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Get started by creating your first waiver.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    waivers.length >= waiverLimit
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                  disabled={waivers.length >= waiverLimit}
                >
                  <PlusIcon className="h-4 w-4 mr-2 pointer-events-none" />
                  <span className="pointer-events-none">{waivers.length >= waiverLimit
                    ? `Limit Reached (${waiverLimit}/${waiverLimit})`
                    : `Create Waiver ${waivers.length}/${waiverLimit}`}</span>
                </button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {waivers.map((waiver) => (
                <li key={waiver.id} className="px-4 xl:px-6 py-4">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="flex items-center">
                      <ClipboardDocumentIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                          {DOMPurify.sanitize(waiver.title, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {submissionCounts[waiver.id] !== undefined
                            ? `${
                                submissionCounts[waiver.id]
                              } students have signed`
                            : "Loading..."}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Updated:{" "}
                          {new Date(waiver.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 xl:space-x-2">

                      <button
                        type="button"
                        onClick={() => {
                          const publicUrl = `${window.location.origin}/waiver/${waiver.id}`;
                          navigator.clipboard.writeText(publicUrl);
                          setNotification({
                            show: true,
                            message: "Public link copied to clipboard",
                            type: "success",
                          });
                        }}
                        className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                        title="Copy public link"
                      >
                        <DocumentDuplicateIcon className="h-5 w-5 text-gray-600 dark:text-gray-300 pointer-events-none" />
                      </button>

                      <a
                        href={`/waiver/${waiver.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                        title="View public form"
                      >
                        <svg
                          className="h-5 w-5 text-blue-500 dark:text-blue-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                      <button
                        type="button"
                        onClick={() => openEditModal(waiver)}
                        className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                        title="Edit waiver"
                      >
                        <PencilIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 pointer-events-none" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteWaiver(waiver)}
                        className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                        title="Delete waiver"
                      >
                        <TrashIcon className="h-5 w-5 text-red-600 dark:text-red-400 pointer-events-none" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openSubmissionsModal(waiver)}
                        className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition w-full xl:w-auto"
                        title="View submissions"
                      >
                        <UserIcon className="h-5 w-5 inline-block mr-1 text-indigo-600 dark:text-indigo-400 pointer-events-none" />
                        <span className="text-indigo-600 dark:text-indigo-400 font-medium pointer-events-none">
                          View Submissions
                        </span>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-11/12 lg:w-11/12 xl:w-10/12 shadow-lg rounded-md bg-white dark:bg-gray-800 max-h-[95vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Create New Waiver
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form Builder */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                      Waiver Title
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          title: e.target.value,
                          fields: formData.fields,
                        })
                      }
                      className={getInputClassName(
                        "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      )}
                      placeholder="e.g., Liability Waiver, Medical Release Form"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                      Waiver Content
                    </label>
                    <textarea
                      required
                      rows={8}
                      value={formData.content}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          content: e.target.value,
                          fields: formData.fields,
                        })
                      }
                      className={getInputClassName(
                        "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      )}
                      placeholder="Enter the main waiver text..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                      Email Settings
                    </label>
                    <div className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
                      <input
                        type="checkbox"
                        id="send-confirmation-email"
                        checked={true}
                        disabled={true}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 cursor-not-allowed opacity-75"
                      />
                      <label htmlFor="send-confirmation-email" className="text-sm font-medium text-gray-700 dark:text-gray-100">
                        Send confirmation email to students
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Students will automatically receive a copy of their completed waiver via email <strong className="text-red-600 dark:text-red-400">(requires Gmail integration)</strong>
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-100">
                        Form Fields
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={openSmsOptInModal}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-800/50"
                        >
                          <PhoneIcon className="h-3 w-3 mr-1" />
                          Add SMS Opt-In
                        </button>
                        <button
                          type="button"
                          onClick={openCustomCheckboxModal}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-800/50"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Add Custom Checkbox
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {AVAILABLE_FIELDS.map((field) => {
                        const isIncluded = formData.fields.some(
                          (f) => f.type === field.type
                        );
                        const includedField = formData.fields.find(
                          (f) => f.type === field.type
                        );
                        const IconComponent = FIELD_ICONS[field.type];

                        return (
                          <div
                            key={field.type}
                            className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md"
                          >
                            <div className="flex items-center space-x-3">
                              <IconComponent className="h-5 w-5 text-gray-400 dark:text-gray-400" />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-100">
                                {field.label}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {isIncluded && (
                                <>
                                  <button
                                    onClick={() => moveField(field.type, "up")}
                                    className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                    title="Move up"
                                  >
                                    <ArrowsUpDownIcon className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      moveField(field.type, "down")
                                    }
                                    className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                    title="Move down"
                                  >
                                    <ArrowsUpDownIcon className="h-4 w-4 -rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                  </button>
                                  <label className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={includedField?.required || false}
                                      onChange={() =>
                                        toggleFieldRequired(field.type)
                                      }
                                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                    />
                                    <span className="ml-2 text-xs text-gray-600 dark:text-gray-200">
                                      Required
                                    </span>
                                  </label>
                                </>
                              )}
                              <button
                                onClick={() => toggleField(field.type)}
                                className={`px-3 py-1 text-xs font-medium rounded-md ${
                                  isIncluded
                                    ? "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-800/50"
                                    : "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-800/50"
                                }`}
                              >
                                <span className="pointer-events-none">{isIncluded ? "Remove" : "Add"}</span>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {/* SMS Opt-In Fields */}
                      {formData.fields
                        .filter((f) => f.type === "smsOptIn")
                        .map((smsField) => {
                          const IconComponent = FIELD_ICONS[smsField.type];
                          const editingState = editingSmsStudioNames[smsField.id] || { editing: false, tempStudioName: smsField.studioName || "" };

                          return (
                            <div
                              key={smsField.id}
                              className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-green-50 dark:bg-green-900/20"
                            >
                              <div className="flex items-start space-x-3 flex-1">
                                <IconComponent className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
                                    SMS Opt-In
                                  </div>
                                  {editingState.editing ? (
                                    <input
                                      type="text"
                                      value={editingState.tempStudioName}
                                      onChange={(e) => setEditingSmsStudioNames({
                                        ...editingSmsStudioNames,
                                        [smsField.id]: { editing: true, tempStudioName: e.target.value }
                                      })}
                                      onBlur={() => {
                                        updateSmsOptInStudioName(smsField.id, editingState.tempStudioName);
                                        setEditingSmsStudioNames({
                                          ...editingSmsStudioNames,
                                          [smsField.id]: { editing: false, tempStudioName: smsField.studioName || "" }
                                        });
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          updateSmsOptInStudioName(smsField.id, editingState.tempStudioName);
                                          setEditingSmsStudioNames({
                                            ...editingSmsStudioNames,
                                            [smsField.id]: { editing: false, tempStudioName: smsField.studioName || "" }
                                          });
                                        } else if (e.key === "Escape") {
                                          setEditingSmsStudioNames({
                                            ...editingSmsStudioNames,
                                            [smsField.id]: { editing: false, tempStudioName: smsField.studioName || "" }
                                          });
                                        }
                                      }}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                      placeholder="Studio Name"
                                      autoFocus
                                    />
                                  ) : (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      Studio: <span
                                        className="cursor-pointer hover:text-green-600 dark:hover:text-green-400 font-medium"
                                        onClick={() => setEditingSmsStudioNames({
                                          ...editingSmsStudioNames,
                                          [smsField.id]: { editing: true, tempStudioName: smsField.studioName || "" }
                                        })}
                                        title="Click to edit studio name"
                                      >
                                        {smsField.studioName || "[Studio Name]"}
                                      </span>
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1 line-clamp-2">
                                    {smsField.label.substring(0, 80)}...
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => moveField(smsField.type, "up", smsField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move up"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <button
                                  onClick={() => moveField(smsField.type, "down", smsField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move down"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 -rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={smsField.required || false}
                                    onChange={() =>
                                      toggleFieldRequired(smsField.type, smsField.id)
                                    }
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                  />
                                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-200">
                                    Required
                                  </span>
                                </label>
                                <button
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      fields: formData.fields.filter((f) => f.id !== smsField.id),
                                    });
                                    // Clean up editing state
                                    const newEditingState = { ...editingSmsStudioNames };
                                    delete newEditingState[smsField.id];
                                    setEditingSmsStudioNames(newEditingState);
                                  }}
                                  className="px-3 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-800/50"
                                >
                                  <span className="pointer-events-none">Remove</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      {/* Custom Checkboxes */}
                      {formData.fields
                        .filter((f) => f.type === "customCheckbox")
                        .map((customField) => {
                          const IconComponent = FIELD_ICONS[customField.type];
                          const editingState = editingCheckboxLabels[customField.id] || { editing: false, tempLabel: customField.label };

                          return (
                            <div
                              key={customField.id}
                              className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-indigo-50 dark:bg-indigo-900/20"
                            >
                              <div className="flex items-center space-x-3 flex-1">
                                <IconComponent className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                                {editingState.editing ? (
                                  <input
                                    type="text"
                                    value={editingState.tempLabel}
                                    onChange={(e) => setEditingCheckboxLabels({
                                      ...editingCheckboxLabels,
                                      [customField.id]: { editing: true, tempLabel: e.target.value }
                                    })}
                                    onBlur={() => {
                                      updateCustomCheckboxLabel(customField.id, editingState.tempLabel);
                                      setEditingCheckboxLabels({
                                        ...editingCheckboxLabels,
                                        [customField.id]: { editing: false, tempLabel: customField.label }
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        updateCustomCheckboxLabel(customField.id, editingState.tempLabel);
                                        setEditingCheckboxLabels({
                                          ...editingCheckboxLabels,
                                          [customField.id]: { editing: false, tempLabel: customField.label }
                                        });
                                      } else if (e.key === "Escape") {
                                        setEditingCheckboxLabels({
                                          ...editingCheckboxLabels,
                                          [customField.id]: { editing: false, tempLabel: customField.label }
                                        });
                                      }
                                    }}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    autoFocus
                                  />
                                ) : (
                                  <span
                                    className="text-sm font-medium text-gray-700 dark:text-gray-100 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400"
                                    onClick={() => setEditingCheckboxLabels({
                                      ...editingCheckboxLabels,
                                      [customField.id]: { editing: true, tempLabel: customField.label }
                                    })}
                                    title="Click to edit label"
                                  >
                                    {customField.label || "Custom Checkbox"}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => moveField(customField.type, "up", customField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move up"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <button
                                  onClick={() => moveField(customField.type, "down", customField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move down"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 -rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={customField.required || false}
                                    onChange={() =>
                                      toggleFieldRequired(customField.type, customField.id)
                                    }
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                  />
                                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-200">
                                    Required
                                  </span>
                                </label>
                                <button
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      fields: formData.fields.filter((f) => f.id !== customField.id),
                                    });
                                    // Clean up editing state
                                    const newEditingState = { ...editingCheckboxLabels };
                                    delete newEditingState[customField.id];
                                    setEditingCheckboxLabels(newEditingState);
                                  }}
                                  className="px-3 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-800/50"
                                >
                                  <span className="pointer-events-none">Remove</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Live Preview
                  </h4>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-md shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {formData.title || "Waiver Title"}
                    </h2>

                    <div className="prose prose-sm max-w-none mb-6">
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {formData.content ||
                          "Your waiver content will appear here..."}
                      </p>
                    </div>

                    {formData.fields.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                          Student Information
                        </h3>
                        {formData.fields.map((field) =>
                          renderFieldPreview(field)
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({
                      title: "",
                      content: "",
                      fields: [],
                      send_confirmation_email: true,
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  onClick={handleCreateWaiver}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                >
                  <span className="pointer-events-none">Create Waiver</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedWaiver && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-11/12 lg:w-11/12 xl:w-10/12 shadow-lg rounded-md bg-white dark:bg-gray-800 max-h-[95vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Edit Waiver
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form Builder */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                      Waiver Title
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          title: e.target.value,
                          fields: formData.fields,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                      Waiver Content
                    </label>
                    <textarea
                      required
                      rows={8}
                      value={formData.content}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          content: e.target.value,
                          fields: formData.fields,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                      Email Settings
                    </label>
                    <div className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
                      <input
                        type="checkbox"
                        id="send-confirmation-email-edit"
                        checked={true}
                        disabled={true}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 cursor-not-allowed opacity-75"
                      />
                      <label htmlFor="send-confirmation-email-edit" className="text-sm font-medium text-gray-700 dark:text-gray-100">
                        Send confirmation email to students
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Students will automatically receive a copy of their completed waiver via email <strong className="text-red-600 dark:text-red-400">(requires Gmail integration)</strong>
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-100">
                        Form Fields
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={openSmsOptInModal}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-800/50"
                        >
                          <PhoneIcon className="h-3 w-3 mr-1" />
                          Add SMS Opt-In
                        </button>
                        <button
                          type="button"
                          onClick={openCustomCheckboxModal}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-800/50"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Add Custom Checkbox
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {AVAILABLE_FIELDS.map((field) => {
                        const isIncluded = formData.fields.some(
                          (f) => f.type === field.type
                        );
                        const includedField = formData.fields.find(
                          (f) => f.type === field.type
                        );
                        const IconComponent = FIELD_ICONS[field.type];

                        return (
                          <div
                            key={field.type}
                            className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md"
                          >
                            <div className="flex items-center space-x-3">
                              <IconComponent className="h-5 w-5 text-gray-400 dark:text-gray-400" />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-100">
                                {field.label}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {isIncluded && (
                                <>
                                  <button
                                    onClick={() => moveField(field.type, "up")}
                                    className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 transition"
                                    title="Move up"
                                  >
                                    <ArrowsUpDownIcon className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      moveField(field.type, "down")
                                    }
                                    className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 transition"
                                    title="Move down"
                                  >
                                    <ArrowsUpDownIcon className="h-4 w-4 -rotate-90 text-gray-500 dark:text-gray-400" />
                                  </button>
                                  <label className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={includedField?.required || false}
                                      onChange={() =>
                                        toggleFieldRequired(field.type)
                                      }
                                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                    />
                                    <span className="ml-2 text-xs text-gray-600 dark:text-gray-200">
                                      Required
                                    </span>
                                  </label>
                                </>
                              )}
                              <button
                                onClick={() => toggleField(field.type)}
                                className={`px-3 py-1 text-xs font-medium rounded-md ${
                                  isIncluded
                                    ? "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-800/50"
                                    : "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-800/50"
                                }`}
                              >
                                {isIncluded ? "Remove" : "Add"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {/* SMS Opt-In Fields */}
                      {formData.fields
                        .filter((f) => f.type === "smsOptIn")
                        .map((smsField) => {
                          const IconComponent = FIELD_ICONS[smsField.type];
                          const editingState = editingSmsStudioNames[smsField.id] || { editing: false, tempStudioName: smsField.studioName || "" };

                          return (
                            <div
                              key={smsField.id}
                              className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-green-50 dark:bg-green-900/20"
                            >
                              <div className="flex items-start space-x-3 flex-1">
                                <IconComponent className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
                                    SMS Opt-In
                                  </div>
                                  {editingState.editing ? (
                                    <input
                                      type="text"
                                      value={editingState.tempStudioName}
                                      onChange={(e) => setEditingSmsStudioNames({
                                        ...editingSmsStudioNames,
                                        [smsField.id]: { editing: true, tempStudioName: e.target.value }
                                      })}
                                      onBlur={() => {
                                        updateSmsOptInStudioName(smsField.id, editingState.tempStudioName);
                                        setEditingSmsStudioNames({
                                          ...editingSmsStudioNames,
                                          [smsField.id]: { editing: false, tempStudioName: smsField.studioName || "" }
                                        });
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          updateSmsOptInStudioName(smsField.id, editingState.tempStudioName);
                                          setEditingSmsStudioNames({
                                            ...editingSmsStudioNames,
                                            [smsField.id]: { editing: false, tempStudioName: smsField.studioName || "" }
                                          });
                                        } else if (e.key === "Escape") {
                                          setEditingSmsStudioNames({
                                            ...editingSmsStudioNames,
                                            [smsField.id]: { editing: false, tempStudioName: smsField.studioName || "" }
                                          });
                                        }
                                      }}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                      placeholder="Studio Name"
                                      autoFocus
                                    />
                                  ) : (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      Studio: <span
                                        className="cursor-pointer hover:text-green-600 dark:hover:text-green-400 font-medium"
                                        onClick={() => setEditingSmsStudioNames({
                                          ...editingSmsStudioNames,
                                          [smsField.id]: { editing: true, tempStudioName: smsField.studioName || "" }
                                        })}
                                        title="Click to edit studio name"
                                      >
                                        {smsField.studioName || "[Studio Name]"}
                                      </span>
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1 line-clamp-2">
                                    {smsField.label.substring(0, 80)}...
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => moveField(smsField.type, "up", smsField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move up"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <button
                                  onClick={() => moveField(smsField.type, "down", smsField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move down"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 -rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={smsField.required || false}
                                    onChange={() =>
                                      toggleFieldRequired(smsField.type, smsField.id)
                                    }
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                  />
                                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-200">
                                    Required
                                  </span>
                                </label>
                                <button
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      fields: formData.fields.filter((f) => f.id !== smsField.id),
                                    });
                                    // Clean up editing state
                                    const newEditingState = { ...editingSmsStudioNames };
                                    delete newEditingState[smsField.id];
                                    setEditingSmsStudioNames(newEditingState);
                                  }}
                                  className="px-3 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-800/50"
                                >
                                  <span className="pointer-events-none">Remove</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      {/* Custom Checkboxes */}
                      {formData.fields
                        .filter((f) => f.type === "customCheckbox")
                        .map((customField) => {
                          const IconComponent = FIELD_ICONS[customField.type];
                          const editingState = editingCheckboxLabels[customField.id] || { editing: false, tempLabel: customField.label };

                          return (
                            <div
                              key={customField.id}
                              className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-indigo-50 dark:bg-indigo-900/20"
                            >
                              <div className="flex items-center space-x-3 flex-1">
                                <IconComponent className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                                {editingState.editing ? (
                                  <input
                                    type="text"
                                    value={editingState.tempLabel}
                                    onChange={(e) => setEditingCheckboxLabels({
                                      ...editingCheckboxLabels,
                                      [customField.id]: { editing: true, tempLabel: e.target.value }
                                    })}
                                    onBlur={() => {
                                      updateCustomCheckboxLabel(customField.id, editingState.tempLabel);
                                      setEditingCheckboxLabels({
                                        ...editingCheckboxLabels,
                                        [customField.id]: { editing: false, tempLabel: customField.label }
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        updateCustomCheckboxLabel(customField.id, editingState.tempLabel);
                                        setEditingCheckboxLabels({
                                          ...editingCheckboxLabels,
                                          [customField.id]: { editing: false, tempLabel: customField.label }
                                        });
                                      } else if (e.key === "Escape") {
                                        setEditingCheckboxLabels({
                                          ...editingCheckboxLabels,
                                          [customField.id]: { editing: false, tempLabel: customField.label }
                                        });
                                      }
                                    }}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    autoFocus
                                  />
                                ) : (
                                  <span
                                    className="text-sm font-medium text-gray-700 dark:text-gray-100 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400"
                                    onClick={() => setEditingCheckboxLabels({
                                      ...editingCheckboxLabels,
                                      [customField.id]: { editing: true, tempLabel: customField.label }
                                    })}
                                    title="Click to edit label"
                                  >
                                    {customField.label || "Custom Checkbox"}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => moveField(customField.type, "up", customField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move up"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <button
                                  onClick={() => moveField(customField.type, "down", customField.id)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="Move down"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 -rotate-90 text-gray-500 dark:text-gray-400 pointer-events-none" />
                                </button>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={customField.required || false}
                                    onChange={() =>
                                      toggleFieldRequired(customField.type, customField.id)
                                    }
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                  />
                                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-200">
                                    Required
                                  </span>
                                </label>
                                <button
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      fields: formData.fields.filter((f) => f.id !== customField.id),
                                    });
                                    // Clean up editing state
                                    const newEditingState = { ...editingCheckboxLabels };
                                    delete newEditingState[customField.id];
                                    setEditingCheckboxLabels(newEditingState);
                                  }}
                                  className="px-3 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-800/50"
                                >
                                  <span className="pointer-events-none">Remove</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Live Preview
                  </h4>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-md shadow-sm">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {formData.title || "Waiver Title"}
                    </h2>

                    <div className="prose prose-sm max-w-none mb-6">
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {formData.content ||
                          "Your waiver content will appear here..."}
                      </p>
                    </div>

                    {formData.fields.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                          Student Information
                        </h3>
                        {formData.fields.map((field) =>
                          renderFieldPreview(field)
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedWaiver(null);
                    setFormData({
                      title: "",
                      content: "",
                      fields: [],
                      send_confirmation_email: true,
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  onClick={handleUpdateWaiver}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                >
                  <span className="pointer-events-none">Update Waiver</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedWaiver && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {selectedWaiver.title}
                </h3>
                <button
                  onClick={() => copyToClipboard(selectedWaiver.content)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <DocumentDuplicateIcon className="h-4 w-4 mr-1 pointer-events-none" />
                  <span className="pointer-events-none">Copy</span>
                </button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md max-h-96 overflow-y-auto">
                <div 
                  className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(selectedWaiver.content, {
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li'],
                      ALLOWED_ATTR: []
                    })
                  }}
                />
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedWaiver(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="pointer-events-none">Close</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Electronic Signature
                </h3>
                <button
                  onClick={closeSignatureModal}
                  className="p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition"
                >
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please sign your name in the box below using your mouse or
                  touch screen.
                </p>

                <div className="border-2 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={200}
                    className="w-full h-48 cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    style={{ touchAction: "none" }}
                  />
                </div>

                <div className="flex justify-between items-center">
                  <button
                    onClick={clearSignature}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2 pointer-events-none" />
                    <span className="pointer-events-none">Clear</span>
                  </button>

                  <div className="space-x-3">
                    <button
                      onClick={closeSignatureModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                    <button
                      onClick={saveSignature}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <span className="pointer-events-none">Save Signature</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {showSubmissionsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 xl:top-8 mx-auto p-4 xl:p-5 border w-11/12 md:w-4/5 lg:w-3/5 shadow-lg rounded-md bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Waiver Submissions
                  {submissionsWaiver ? `: ${submissionsWaiver.title}` : ""}
                </h3>
                {submissionsWaiver && (
                  <div className="flex items-center space-x-2 mt-1">
                    <DocumentTextIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {submissionCounts[submissionsWaiver.id] || 0} signed waivers
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {submissionsWaiver && (
                  <button
                    type="button"
                    onClick={() => exportWaiverData(submissionsWaiver)}
                    className="px-3 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 cursor-pointer flex items-center w-full xl:w-auto justify-center"
                  >
                    <svg className="h-4 w-4 mr-1 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="pointer-events-none">Export Data</span>
                  </button>
                )}
                <button
                  onClick={closeSubmissionsModal}
                  className="p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition"
                >
                  <XMarkIcon className="h-6 w-6 pointer-events-none" />
                </button>
              </div>
            </div>
            <div className="mb-4 flex items-center">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name..."
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 w-full dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            {submissionsLoading ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                Loading submissions...
              </div>
            ) : submissionsError ? (
              <div className="text-center text-red-500 dark:text-red-400 py-8">
                {submissionsError}
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                No submissions found.
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden xl:block overflow-x-auto mb-4">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase">
                          Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase">
                          Email
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase">
                          Submitted At
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((sub) => (
                        <tr
                          key={sub.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="px-4 py-2 text-gray-900 dark:text-white">
                            {sub.student_info?.name ||
                              sub.student_info?.fullName ||
                              sub.student_info?.full_name ||
                              sub.student_info?.firstName ||
                              sub.student_info?.first_name ||
                              "-"}
                          </td>
                          <td className="px-4 py-2 text-gray-900 dark:text-white">
                            {sub.student_info?.email ||
                              sub.student_info?.emailAddress ||
                              sub.student_info?.email_address ||
                              "-"}
                          </td>
                          <td className="px-4 py-2 text-gray-900 dark:text-white">
                            {new Date(sub.submitted_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <button
                                className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-indigo-600 dark:text-indigo-400 font-medium"
                                onClick={() => openSubmissionDetail(sub.id)}
                              >
                                <span className="pointer-events-none">View</span>
                              </button>
                              <button
                                className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400 font-medium"
                                onClick={() => setSubmissionToDelete(sub)}
                              >
                                <span className="pointer-events-none">Delete</span>
                              </button>
                              {sub.converted_to_lead || sub.converted_to_student ? (
                                <span className="p-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-500 dark:text-gray-400 font-medium">
                                  {sub.converted_to_lead && sub.converted_to_student
                                    ? "Converted to Both"
                                    : sub.converted_to_lead
                                    ? "Converted to Lead"
                                    : "Converted to Student"}
                                </span>
                              ) : (
                                <>
                                  <button
                                    className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-green-600 dark:text-green-400 font-medium"
                                    onClick={() => convertToLead(sub)}
                                  >
                                    <span className="pointer-events-none">Make Lead</span>
                                  </button>
                                  <button
                                    className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-blue-600 dark:text-blue-400 font-medium"
                                    onClick={() => convertToStudent(sub)}
                                  >
                                    <span className="pointer-events-none">Make Student</span>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Card View */}
                <div className="xl:hidden space-y-4 mb-4">
                  {submissions.map((sub) => (
                    <div
                      key={sub.id}
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="space-y-2 mb-4">
                        <div>
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Name</span>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {sub.student_info?.name ||
                              sub.student_info?.fullName ||
                              sub.student_info?.full_name ||
                              sub.student_info?.firstName ||
                              sub.student_info?.first_name ||
                              "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Email</span>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {sub.student_info?.email ||
                              sub.student_info?.emailAddress ||
                              sub.student_info?.email_address ||
                              "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Submitted At</span>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {new Date(sub.submitted_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <button
                          className="w-full p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-indigo-600 dark:text-indigo-400 font-medium"
                          onClick={() => openSubmissionDetail(sub.id)}
                        >
                          <span className="pointer-events-none">View</span>
                        </button>
                        <button
                          className="w-full p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400 font-medium"
                          onClick={() => setSubmissionToDelete(sub)}
                        >
                          <span className="pointer-events-none">Delete</span>
                        </button>
                        {sub.converted_to_lead || sub.converted_to_student ? (
                          <span className="w-full p-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-500 dark:text-gray-400 font-medium text-center">
                            {sub.converted_to_lead && sub.converted_to_student
                              ? "Converted to Both"
                              : sub.converted_to_lead
                              ? "Converted to Lead"
                              : "Converted to Student"}
                          </span>
                        ) : (
                          <>
                            <button
                              className="w-full p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-green-600 dark:text-green-400 font-medium"
                              onClick={() => convertToLead(sub)}
                            >
                              <span className="pointer-events-none">Make Lead</span>
                            </button>
                            <button
                              className="w-full p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-blue-600 dark:text-blue-400 font-medium"
                              onClick={() => convertToStudent(sub)}
                            >
                              <span className="pointer-events-none">Make Student</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {/* Submission detail modal */}
            {selectedSubmission &&
              (() => {
                const eSignatureValue =
                  selectedSubmission.student_info?.eSignature ||
                  selectedSubmission.student_info?.signature ||
                  selectedSubmission.form_data?.eSignature ||
                  "";
                return (
                  <div className="fixed inset-0 bg-gray-700 bg-opacity-60 flex items-center justify-center z-60 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl relative max-h-[90vh] flex flex-col">
                      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => setSelectedSubmission(null)}
                          className="absolute top-2 right-2 p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition"
                        >
                          <XMarkIcon className="h-6 w-6 pointer-events-none" />
                        </button>
                        <h4 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                          Submission Details
                        </h4>
                        <div className="mb-2 text-sm text-gray-900 dark:text-white">
                          Submitted:{" "}
                          {new Date(
                            selectedSubmission.submitted_at
                          ).toLocaleString()}
                        </div>
                      </div>
                      <div className="p-6 overflow-y-auto flex-1">
                      <div className="space-y-2">
                        {Object.entries(selectedSubmission.student_info || {})
                          .filter(
                            ([key]) =>
                              !["eSignature", "signature"].includes(key)
                          )
                          .map(([key, value]) => (
                            <div
                              key={key}
                              className="text-gray-900 dark:text-white"
                            >
                              <span className="font-semibold capitalize">
                                {key.replace(/([A-Z])/g, " $1")}:
                              </span>{" "}
                              {typeof value === "string"
                                ? value
                                : JSON.stringify(value)}
                            </div>
                          ))}
                      </div>
                      {selectedSubmission.form_data && (
                        <div className="mt-4">
                          <h5 className="font-semibold mb-2 text-gray-900 dark:text-white">
                            Form Data
                          </h5>
                          <div className="space-y-2">
                            {(() => {
                              // Create a map to track which fields we've already displayed
                              const displayedFields = new Set<string>();
                              const formDataEntries: JSX.Element[] = [];
                              
                              // First, display all existing form_data fields
                              Object.entries(selectedSubmission.form_data).forEach(([key, value]) => {
                                let displayLabel = key.replace(/([A-Z])/g, " $1").trim();
                                
                                // Check if this is a custom field (customCheckbox or smsOptIn)
                                if (submissionsWaiver?.fields) {
                                  const field = submissionsWaiver.fields.find(f => f.id === key);
                                  if (field) {
                                    displayLabel = field.label;
                                    displayedFields.add(field.id);
                                  }
                                }
                                
                                // Check if this is a signature field
                                const isSignature = ['eSignature', 'signature'].includes(key);
                                
                                // Format the value
                                let displayValue: string;
                                if (typeof value === 'boolean') {
                                  displayValue = value ? '✓ Yes' : '✗ No';
                                } else if (typeof value === 'string') {
                                  displayValue = value;
                                } else {
                                  displayValue = JSON.stringify(value);
                                }
                                
                                formDataEntries.push(
                                  <div key={key} className="text-gray-900 dark:text-white">
                                    <span className="font-semibold capitalize">{displayLabel}:</span>{" "}
                                    {isSignature ? (
                                      <div className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded overflow-x-auto">
                                        <code className="text-xs break-all whitespace-pre-wrap">{displayValue}</code>
                                      </div>
                                    ) : (
                                      <span>{displayValue}</span>
                                    )}
                                  </div>
                                );
                              });
                              
                              // Then, add any missing custom checkbox/SMS fields from the waiver definition
                              if (submissionsWaiver?.fields) {
                                submissionsWaiver.fields.forEach((field: WaiverField) => {
                                  if ((field.type === 'customCheckbox' || field.type === 'smsOptIn') && !displayedFields.has(field.id)) {
                                    formDataEntries.push(
                                      <div key={field.id} className="text-gray-900 dark:text-white">
                                        <span className="font-semibold capitalize">{field.label}:</span>{" "}
                                        <span>✗ No</span>
                                      </div>
                                    );
                                  }
                                });
                              }
                              
                              return formDataEntries;
                            })()}
                          </div>
                        </div>
                              )}
                      </div>
                      </div>
                    </div>
                );
              })()}

            {/* Delete Submission Confirmation Modal */}
            {submissionToDelete && (
              <div className="fixed inset-0 bg-gray-700 bg-opacity-60 flex items-center justify-center z-70 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md relative">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={cancelDeleteSubmission}
                    >
                      <span className="sr-only pointer-events-none">Close</span>
                      <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                    </button>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Delete Submission
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this waiver submission? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                    <button
                      type="button"
                      onClick={cancelDeleteSubmission}
                      className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <span className="pointer-events-none">Cancel</span>
                    </button>
                    <button
                      type="button"
                      onClick={confirmDeleteSubmission}
                      className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <span className="pointer-events-none">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {waiverToDelete && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={cancelDelete}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                  Delete Waiver
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to delete "{waiverToDelete.title}"?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <span className="pointer-events-none">Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        onClose={closeNotification}
        type={notification.type as any}
      />

      {/* Custom Checkbox Creation Modal */}
      {showCustomCheckboxModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={closeCustomCheckboxModal}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                  Add Custom Checkbox
                </h3>
                  <div className="mt-4">
                    <label htmlFor="checkbox-label" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Checkbox Label Text
                    </label>
                    <input
                      type="text"
                      id="checkbox-label"
                      value={newCheckboxLabel}
                      onChange={(e) => setNewCheckboxLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          addCustomCheckbox();
                        } else if (e.key === "Escape") {
                          closeCustomCheckboxModal();
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Enter checkbox label text..."
                      autoFocus
                    />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      This text will appear next to the checkbox in the waiver form.
                    </p>
                  </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  type="button"
                  onClick={closeCustomCheckboxModal}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={addCustomCheckbox}
                  disabled={!newCheckboxLabel || !newCheckboxLabel.trim()}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="pointer-events-none">Add Checkbox</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMS Opt-In Creation Modal */}
      {showSmsOptInModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  onClick={closeSmsOptInModal}
                >
                  <span className="sr-only pointer-events-none">Close</span>
                  <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                </button>
              </div>
              <div>
                <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                  Add SMS Opt-In Checkbox
                </h3>
                  <div className="mt-4">
                    <label htmlFor="studio-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Studio Name
                    </label>
                    <input
                      type="text"
                      id="studio-name"
                      value={smsOptInStudioName}
                      onChange={(e) => setSmsOptInStudioName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          addSmsOptIn();
                        } else if (e.key === "Escape") {
                          closeSmsOptInModal();
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Enter your studio name..."
                      autoFocus
                    />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      This will be inserted into the SMS opt-in text: "I agree to receive SMS messages from <strong>[your studio name]</strong> regarding class reminders, student alerts, and schedule updates..."
                    </p>
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                        Preview: "I agree to receive SMS messages from {smsOptInStudioName || "[Studio Name]"} regarding class reminders, student alerts, and schedule updates. Message frequency varies. Standard message and data rates may apply. I understand I can reply STOP at any time to unsubscribe."
                      </p>
                    </div>
                  </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  type="button"
                  onClick={closeSmsOptInModal}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={addSmsOptIn}
                  className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <span className="pointer-events-none">Add SMS Opt-In</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
